'use client'

import { Button } from '@/components/ui/button'
import { ActionIcon, Badge, Card, CardSection, Grid, Image, Text, Tooltip } from '@mantine/core'
import { IconMinus, IconPlus } from '@tabler/icons-react'
import { formatToBRL } from 'brazilian-values'

interface Product {
	rating: number | null | undefined
	category: number | null | undefined
	popularity: number | null | undefined
	what: string | null | undefined
	quantity: number | null | undefined
	howMuch: number | null | undefined
	image: string | null | undefined
	id: number
}

interface ProductCardProps {
	product: Product
	cartQuantity: number
	onIncrement: () => void
	onDecrement: () => void
}

export function ProductCard({ product, cartQuantity, onIncrement, onDecrement }: ProductCardProps) {
	const { image, howMuch, id, what, quantity } = product
	const availableQuantity = quantity || 0
	const canIncrement = cartQuantity < availableQuantity
	const isOutOfStock = availableQuantity === 0

	return (
		<Grid.Col span={{ base: 6, xs: 6, sm: 6, md: 4, lg: 3, xl: 2.4 }} key={`products-${id}`}>
			<div className="relative group h-full">
				<Card
					radius="lg"
					h="100%"
					miw={{ base: '100%', xs: '200px', md: '220px' }}
					mih={{ base: '380px', md: '430px' }}
					shadow="sm"
					className={`transition-all duration-300 group-hover:shadow-xl ${!isOutOfStock ? 'group-hover:scale-[1.02]' : ''} overflow-hidden py-4 ${cartQuantity > 0 ? 'border-light_purple border-2 scale-[1.03] shadow-lg shadow-light_purple/20' : 'group-hover:border-light_purple'} ${isOutOfStock ? 'opacity-60' : ''}`}
					withBorder
				>
					<CardSection className="px-4 py-4 h-full flex flex-col items-center justify-between">
						{/* Stock Badge */}
						<div className="absolute top-2 right-2">
							<Badge color={availableQuantity > 5 ? 'green' : availableQuantity > 0 ? 'yellow' : 'red'} variant="filled" size="lg">
								{isOutOfStock ? 'Presenteado' : `${availableQuantity} disponív${availableQuantity !== 1 ? 'eis' : 'el'}`}
							</Badge>
						</div>

						{/* Product Image */}
						<div className="flex items-center justify-center flex-grow py-3">
							<Image
								src={image}
								alt={what ?? ''}
								fit="contain"
								className="object-center max-h-[160px] md:max-h-[200px] w-auto transition-transform duration-300"
							/>
						</div>

						{/* Product Name */}
						<div className="w-full mt-4 h-12 flex items-center justify-center">
							<Tooltip label={what} position="top" withArrow>
								<Text className="w-full break-words text-center font-medium md:text-base transition-colors duration-300 group-hover:text-light_purple line-clamp-2 leading-tight">
									{what}
								</Text>
							</Tooltip>
						</div>

						{/* Price */}
						<div className="mt-3 mb-4">
							<Text className="text-center text-xl font-bold text-light_purple">{formatToBRL(howMuch ?? 0)}</Text>
						</div>

						{/* Counter System */}
						{!isOutOfStock && (
							<div className="w-full mt-auto">
								{cartQuantity === 0 ? (
									/* Add to Cart Button */
									<Button onClick={onIncrement} variant="none" className="w-full bg-light_purple  text-white  hover:scale-105">
										<div className="flex items-center justify-center gap-2">
											<IconPlus size={16} className="" /> Adicionar!
										</div>
									</Button>
								) : (
									/* Quantity Counter */
									<div className="flex items-center justify-center gap-2 w-full">
										<ActionIcon
											size="md"
											radius="xl"
											variant="filled"
											styles={theme => ({
												root: {
													background: theme.colors?.purple?.[5] ?? 'purple',
												},
											})}
											onClick={onDecrement}
											className="shadow-md hover:scale-110 transition-all "
											disabled={cartQuantity === 0}
										>
											<IconMinus size={18} />
										</ActionIcon>

										<div className="flex flex-col items-center min-w-[60px]">
											<Badge color="" variant="filled" size="lg" className=" font-bold">
												{cartQuantity}
											</Badge>
										</div>

										<ActionIcon
											size="md"
											radius="xl"
											variant="filled"
											styles={theme => ({
												root: {
													background: theme.colors?.wine?.[5] ?? 'wine',
												},
											})}
											onClick={onIncrement}
											disabled={!canIncrement}
											className={`shadow-md transition-all duration-200 ${canIncrement ? 'hover:scale-110' : 'opacity-50 cursor-not-allowed'}`}
										>
											<IconPlus size={18} />
										</ActionIcon>
									</div>
								)}

								{!canIncrement && cartQuantity > 0 && (
									<span className="text-center text-xs md:text-sm text-red-500 mt-2">Quantidade máxima atingida</span>
								)}
							</div>
						)}
					</CardSection>
				</Card>
			</div>
		</Grid.Col>
	)
}
